import React from "react";
import { useOutletContext } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";

import PageAbstract from "components/PageAbstract";
import _request from "actions/_request";
import i18n from "i18";
import { isLowBalance } from "utils/aiCredits";
import AppContext from "../../AppContext";
import BuyCreditsModal from "../../components/BuyCreditsModal";

const TYPE_COLORS = {
  use: { bg: "#FE848420", color: "#FE8484" },
  purchase: { bg: "#42C0C720", color: "#42C0C7" },
  grant: { bg: "#4CAF5020", color: "#4CAF50" },
  admin: { bg: "#9FA3BD20", color: "#9FA3BD" },
  adjust: { bg: "#9FA3BD20", color: "#9FA3BD" }, // grey — wallet adjustment/merge
  reserve: { bg: "#F5A62320", color: "#F5A623" }, // amber — credits held
  release: { bg: "#42C0C720", color: "#42C0C7" }, // teal — hold returned
  expire: { bg: "#9FA3BD20", color: "#9FA3BD" }, // grey — bucket expired
};

// Fallback label for any ledger type/action without an explicit translation, so a
// new server-side code never surfaces a raw i18n key (e.g. "adjust" → "Adjust").
const humanize = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

class CreditsPage extends PageAbstract {
  static contextType = AppContext;

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      history: [],
      historyLoading: false,
      page: 1,
      lastPage: 1,
      total: 0,
      buyModalOpen: false,
    };
  }

  componentDidMount() {
    this.loadHistory(1);
  }

  async loadHistory(page) {
    this.setState({ historyLoading: true });
    const res = await _request.aiCredits.getHistory(page, 20);
    if (res?.isOk && res?.data) {
      const { data, last_page, total } = res.data;
      this.setState({ history: data || [], lastPage: last_page || 1, total: total || 0, page });
    }
    this.setState({ historyLoading: false });
  }

  tr(key) {
    return i18n.t("aiCredits." + key);
  }

  renderBalanceCard(ai_credits) {
    const {
      monthly_remaining = 0,
      monthly_allowance = 0,
      monthly_used = 0,
      purchased_balance = 0,
      promotional_balance = 0,
      reserved_balance = 0,
      balance = 0,
      usage_pct,
      reset_at,
    } = ai_credits || {};

    const isLow = isLowBalance(ai_credits);
    // Prefer the backend's usage_pct; fall back to a local ratio for older summaries.
    const usagePct =
      typeof usage_pct === "number"
        ? Math.min(100, Math.max(0, Math.round(usage_pct)))
        : monthly_allowance > 0
          ? Math.min(100, Math.round((monthly_used / monthly_allowance) * 100))
          : 0;

    const cardSx = {
      flex: "1 1 220px",
      p: 3,
      borderRadius: "12px",
      border: "1px solid var(--divider-color, #4f5154)",
      backgroundColor: "var(--hedear-bg)",
    };

    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mb: 4,
        }}>
        {/* Available balance */}
        <Box
          sx={{
            ...cardSx,
            border: `1px solid ${isLow ? "#FE848460" : "#42C0C760"}`,
            backgroundColor: isLow ? "#FE848410" : "#42C0C710",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: isLow ? "#FE8484" : "#42C0C7" }} />
            <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7 }}>
              {this.tr("balanceAvailable")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 32, fontWeight: 700, color: isLow ? "#FE8484" : "#42C0C7" }}>
            {balance}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.6, mt: 0.5 }}>
            {this.tr("credits")}
          </Typography>
          {reserved_balance > 0 && (
            <Typography sx={{ fontSize: 12, color: "#F5A623", mt: 0.5 }}>
              {reserved_balance} {this.tr("reserved").toLowerCase()} · {this.tr("reservedHint")}
            </Typography>
          )}
        </Box>

        {/* Monthly allowance + usage bar */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7, mb: 1 }}>
            {this.tr("monthlyAllowance")}
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "var(--text-color)" }}>
            {monthly_remaining} <Typography component="span" sx={{ fontSize: 14, opacity: 0.5 }}>/ {monthly_allowance}</Typography>
          </Typography>
          {monthly_allowance > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={usagePct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "var(--divider-color, #4f5154)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: isLow ? "#FE8484" : "#42C0C7",
                    borderRadius: 3,
                  },
                }}
              />
              <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.6, mt: 0.5 }}>
                {usagePct}% {this.tr("usageLabel")} · {this.tr("monthlyUsed")}: {monthly_used}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Purchased credits + reset date */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7, mb: 1 }}>
            {this.tr("purchased")}
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "var(--text-color)" }}>
            {purchased_balance}
          </Typography>
          {reset_at && (
            <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.6, mt: 0.5 }}>
              {this.tr("resetAt")}: {moment(reset_at).format("DD.MM.YYYY")}
            </Typography>
          )}
        </Box>

        {/* Promotional credits — only when present */}
        {promotional_balance > 0 && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7, mb: 1 }}>
              {this.tr("promotional")}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#4CAF50" }}>
              {promotional_balance}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "var(--text-color)", opacity: 0.6, mt: 0.5 }}>
              {this.tr("credits")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  renderLowBalanceBanner(ai_credits, isTrial) {
    if (isTrial || !isLowBalance(ai_credits)) return null;

    const pct =
      typeof ai_credits?.usage_pct === "number"
        ? Math.min(100, Math.max(0, Math.round(ai_credits.usage_pct)))
        : 100;

    return (
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => this.setState({ buyModalOpen: true })}
            sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
            {this.tr("lowBalanceCta")}
          </Button>
        }
        sx={{ mb: 3, alignItems: "center" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
          {this.tr("lowBalanceTitle")}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {i18n.t("aiCredits.lowBalanceBody", { pct })}
        </Typography>
      </Alert>
    );
  }

  renderTypeChip(type) {
    const cfg = TYPE_COLORS[type] || TYPE_COLORS.admin;
    const label = i18n.t(`aiCredits.type.${type}`, { defaultValue: humanize(type) });
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: cfg.bg,
          color: cfg.color,
          fontWeight: 600,
          fontSize: 11,
          border: `1px solid ${cfg.color}40`,
        }}
      />
    );
  }

  renderHistory() {
    const { history, historyLoading, page, lastPage } = this.state;

    return (
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "var(--text-color)", mb: 2 }}>
          {this.tr("historyTitle")}
        </Typography>

        {historyLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#42C0C7" }} />
          </Box>
        ) : history.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: "var(--text-color)",
              opacity: 0.5,
            }}>
            <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>{this.tr("historyEmpty")}</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid var(--divider-color, #4f5154)",
                overflow: "hidden",
              }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "var(--hedear-bg)" }}>
                    <TableCell sx={{ color: "var(--text-color)", opacity: 0.7, fontWeight: 600, fontSize: 12 }}>
                      {this.tr("histColDate")}
                    </TableCell>
                    <TableCell sx={{ color: "var(--text-color)", opacity: 0.7, fontWeight: 600, fontSize: 12 }}>
                      {this.tr("histColType")}
                    </TableCell>
                    <TableCell sx={{ color: "var(--text-color)", opacity: 0.7, fontWeight: 600, fontSize: 12 }}>
                      {this.tr("histColAction")}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "var(--text-color)", opacity: 0.7, fontWeight: 600, fontSize: 12 }}>
                      {this.tr("histColAmount")}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "var(--text-color)", opacity: 0.7, fontWeight: 600, fontSize: 12 }}>
                      {this.tr("histColBalance")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        "&:hover": { backgroundColor: "var(--hedear-bg)" },
                        borderTop: "1px solid var(--divider-color, #4f5154)",
                      }}>
                      <TableCell sx={{ color: "var(--text-color)", fontSize: 13 }}>
                        {moment(row.created_at).format("DD.MM.YYYY HH:mm")}
                      </TableCell>
                      <TableCell>
                        {this.renderTypeChip(row.type)}
                      </TableCell>
                      <TableCell sx={{ color: "var(--text-color)", fontSize: 13 }}>
                        {i18n.t(`aiCredits.action.${row.action}`, {
                          defaultValue: humanize(row.action),
                        })}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 14,
                            color:
                              row.amount === 0
                                ? "var(--text-color)"
                                : row.amount < 0
                                  ? "#FE8484"
                                  : "#4CAF50",
                            opacity: row.amount === 0 ? 0.4 : 1,
                          }}>
                          {row.amount === 0
                            ? "—"
                            : `${row.amount > 0 ? "+" : ""}${row.amount}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: "var(--text-color)", fontSize: 13 }}>
                        {row.balance_after}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {lastPage > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={lastPage}
                  page={page}
                  onChange={(_, p) => this.loadHistory(p)}
                  sx={{
                    "& .MuiPaginationItem-root": { color: "var(--text-color)" },
                    "& .Mui-selected": { backgroundColor: "#42C0C720 !important", color: "#42C0C7" },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  render() {
    const { metaData, refreshCredits } = this.context;
    const ai_credits = metaData?.ai_credits;
    const isTrial = ai_credits?.monthly_allowance === 0;
    const { buyModalOpen } = this.state;

    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: "#42C0C7", fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "var(--text-color)" }}>
              {this.tr("pageTitle")}
            </Typography>
          </Box>
          {!isTrial && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => this.setState({ buyModalOpen: true })}
              sx={{
                backgroundColor: "#42C0C7",
                "&:hover": { backgroundColor: "#3aa8ae" },
                fontWeight: 600,
              }}>
              {this.tr("buy")}
            </Button>
          )}
        </Box>

        {this.renderLowBalanceBanner(ai_credits, isTrial)}
        {this.renderBalanceCard(ai_credits)}
        {this.renderHistory()}

        <BuyCreditsModal
          open={buyModalOpen}
          onClose={() => {
            this.setState({ buyModalOpen: false });
            if (refreshCredits) refreshCredits();
            this.loadHistory(1);
          }}
          isTrial={isTrial}
        />
      </Box>
    );
  }
}

function CreditsPageWrapper(props) {
  const outletContext = useOutletContext();
  return <CreditsPage {...props} {...outletContext} />;
}

export default CreditsPageWrapper;
